import { ProjectType } from '@lopo-tech/lopo-api'
import {
  SETTINGS_ACTION_ADD_EMAIL,
  SETTINGS_ACTION_CHANGE,
  SETTINGS_ACTION_LOGIN,
  SETTINGS_ACTION_REGISTRATION,
  SETTINGS_REFERRAL_APPLE,
  SETTINGS_REFERRAL_EMAIL,
  SETTINGS_REFERRAL_FACEBOOK
} from 'src/constants/settings'
import { getConfigValue } from 'src/utils/config/config'

// ---------- API URL paths -----------

const apiHostURL = (path: string) => {
  return process.env.API_HOST ? new URL(path, process.env.API_HOST).href : path
}

// const API_BASE = '/api/';
const API_BASE = apiHostURL('/api/')

export const API = API_BASE + ''
export const API_EVENTSFEED_PATH = API_BASE + 'events/'

export const API_CONVERSATIONS_PATH = API_BASE + 'v2/conversations/'
export const API_LOCATION_SHAPES_KML_PATH = API_BASE + 'location_shapes/kml.xml'
export const API_OGLINKS_PATH = API_BASE + 'oglinks/fetch'
export const API_PLACE_SUGGESTIONS_PATH = API_BASE + 'place_suggestions'
export const API_USER_RESEND_CONFIRMATION_EMAIL = API_BASE + 'profile/send_confirmation_instructions'

// ---------- OAuth2 URL paths ----------

export const OAUTH2_REVOKE_PATH = apiHostURL('/oauth/revoke')
export const OAUTH2_TOKEN_PATH = apiHostURL('/oauth/token')
const OAUTH2_APPLE_PATH = apiHostURL('/omniauth/apple')
const OAUTH2_FACEBOOK_PATH = apiHostURL('/omniauth/facebook')
const OAUTH2_INTENTION_LOGIN = 'login'
const OAUTH2_INTENTION_REGISTER = 'register'
export const OAUTH2_FACEBOOK_LOGIN_URL = (oauthClientId: string, redirectUrl: string) =>
  `${OAUTH2_FACEBOOK_PATH}?intent=${OAUTH2_INTENTION_LOGIN}` +
  `&client_id=${oauthClientId}&redirect_url=${redirectUrl}`
export const OAUTH2_FACEBOOK_REGISTRATION_URL = (googlePlaceId: string, redirectUrl: string, email?: string) =>
  `${OAUTH2_FACEBOOK_PATH}?intent=${OAUTH2_INTENTION_REGISTER}` +
  `&google_place_id=${googlePlaceId}&redirect_url=${redirectUrl}` +
  (
    email
      ? `&email=${email}&redirect_url_email_confirmation=${REDIRECT_URL_REGISTRATION_FACEBOOK_WITH_EMAIL}`
      : ''
  )
export const OAUTH2_APPLE_LOGIN_URL = (oauthClientId: string, redirectUrl: string) =>
  `${OAUTH2_APPLE_PATH}?intent=${OAUTH2_INTENTION_LOGIN}` +
  `&client_id=${oauthClientId}&redirect_url=${redirectUrl}`
export const OAUTH2_APPLE_REGISTRATION_URL = (googlePlaceId: string, redirectUrl: string) =>
  `${OAUTH2_APPLE_PATH}?intent=${OAUTH2_INTENTION_REGISTER}` +
  `&google_place_id=${googlePlaceId}&redirect_url=${redirectUrl}`

// ---------- route partials ------------

export const NEWS_FEED_PARTIAL = '/aktuelles'
export const EVENTS_FEED_PARTIAL = '/veranstaltungen'
export const FOLLOWERS_PARTIAL = '/abonnenten'

export const PROFILE_LIST_AUTHORITY_URI = '/oeffentlich'
export const PROFILE_LIST_BLOG_URI = '/seiten'
export const PROFILE_LIST_PRESS_URI = '/redaktion'
export const PROFILE_LIST_PRIVATE_URI = '/nachbarn'
export const PROFILE_LIST_URIS = [
  PROFILE_LIST_AUTHORITY_URI,
  PROFILE_LIST_BLOG_URI,
  PROFILE_LIST_PRESS_URI,
  PROFILE_LIST_PRIVATE_URI
] as const

export const PUBLIC_PARTICIPATION_PARTIAL = '/public-participation'
export const INFRASTRUCTURE_PROJECTS_PARTIAL = '/infrastructure-projects'
export const PROJECT_LIST_PARTIAL = '/all'
export const PROJECT_MY_LIST_PARTIAL = '/my-projects'
export const PROJECTS_MILESTONES_PARTIAL = '/milestones'

const PROJECT_TYPE_ROUTE_PARTIAL: Record<ProjectType, string> = {
  [ProjectType.PublicParticipationProject]: PUBLIC_PARTICIPATION_PARTIAL,
  [ProjectType.InfrastructureProject]: INFRASTRUCTURE_PROJECTS_PARTIAL
}

export const getProjectRoutes = (type: ProjectType) => {
  const base = PROJECT_TYPE_ROUTE_PARTIAL[type]
  return {
    base,
    overview: (locationSlug: string) => `/${locationSlug}${base}`,
    list: (locationSlug: string) => `/${locationSlug}${base}${PROJECT_LIST_PARTIAL}`,
    myList: (locationSlug: string) => `/${locationSlug}${base}${PROJECT_MY_LIST_PARTIAL}`,
    detail: (slug: string) => `${base}/${slug}`,
    map: (slug: string) => `${base}/${slug}/map`,
    meetings: (slug: string) => `${base}/${slug}/meetings`,
    milestones: (slug: string) => `${base}/${slug}${PROJECTS_MILESTONES_PARTIAL}`
  }
}

// ---------- frontend URL paths ----------

const AUTHOR_TYPE_AUTHORITY_ENGLISH_URI = '/authority/'
const AUTHOR_TYPE_BLOG_ENGLISH_URI = '/blog/'
const AUTHOR_TYPE_REPORTER_ENGLISH_URI = '/reporter/'
const AUTHOR_TYPE_PRIVATE_ENGLISH_URI = '/private/'
const AUTHOR_TYPE_PRESS_ENGLISH_URI = '/press/'
export const FRONTEND_PROFILE_FEED_PATH = '/profiles'
export const FRONTEND_AUTHORITY_PROFILE_FEED_PATH = `${FRONTEND_PROFILE_FEED_PATH}${AUTHOR_TYPE_AUTHORITY_ENGLISH_URI}`
export const FRONTEND_BLOG_PROFILE_FEED_PATH = `${FRONTEND_PROFILE_FEED_PATH}${AUTHOR_TYPE_BLOG_ENGLISH_URI}`
export const FRONTEND_REPORTER_PROFILE_FEED_PATH = `${FRONTEND_PROFILE_FEED_PATH}${AUTHOR_TYPE_REPORTER_ENGLISH_URI}`
export const FRONTEND_PRIVATE_PROFILE_FEED_PATH = `${FRONTEND_PROFILE_FEED_PATH}${AUTHOR_TYPE_PRIVATE_ENGLISH_URI}`
export const FRONTEND_PRESS_PROFILE_FEED_PATH = `${FRONTEND_PROFILE_FEED_PATH}${AUTHOR_TYPE_PRESS_ENGLISH_URI}`
export const FRONTEND_NEWSFEED_PATH = '/newsfeed'
export const FRONTEND_EVENT_PATH = '/events'
export const FRONTEND_PROJECT_PATH = PUBLIC_PARTICIPATION_PARTIAL
export const FRONTEND_NOTIFICATIONS_PATH = '/benachrichtigungen'
export const FRONTEND_POST_PATH = '/posts'
export const FRONTEND_ARTICLE_PATH = '/articles'
export const FRONTEND_SNAPSHOT_PATH = '/snapshots'
export const FRONTEND_CHAT_PATH = '/chat'
export const FRONTEND_POST_COMMENTS_ANCHOR = '#comments'
export const FRONTEND_CONVERSATION_PATH = '/conversations/'
export const FRONTEND_SEND_MESSAGE_CONVERSATION_PATH = '/conversations/open/'
export const FRONTEND_GROUP_OVERVIEW_PATH = '/groups/'
export const FRONTEND_GROUP_PATH = '/groups/'
export const FRONTEND_SETTINGS_PATH = '/settings'
export const FRONTEND_EDIT_DESCRIPTION_PATH = '/settings/description'

export const FRONTEND_EDIT_ABOUT_ME_PATH = '/settings/about-me'
export const FRONTEND_EDIT_ABOUT_ADD_VIDEO_LINK = '/settings/add-link/video'
export const FRONTEND_EDIT_ABOUT_ADD_AUDIO_LINK = '/settings/add-link/audio'
export const FRONTEND_EDIT_INTERNET_SITE_PATH = '/settings/internet-site'
export const FRONTEND_EDIT_TAGS_PATH = '/settings/tags'
export const FRONTEND_EDIT_IMPRESSUM_PATH = '/settings/site-notice'
export const FRONTEND_SETTINGS_VERIFICATION = '/settings/verification'
export const FRONTEND_CREATE_PROFILE = '/create-profile'
export const FRONTEND_LOCALIZED_NEWS = '/neuigkeiten'
export const FRONTEND_LOCALIZED_EVENTS = '/veranstaltungen'
export const FRONTEND_NEW_PASSWORD = '/new-password'
export const FRONTEND_REDIRECT = '/redirect'
export const FRONTEND_NOT_FOUND_PATH = '/not-found'
export const FRONTEND_REGISTRATION = '/registration'
export const FRONTEND_APP_REGISTRATION = '/app-registration'
export const FRONTEND_LOGIN = '/login'
export const FRONTEND_PROFILES_DIRECTORY = '/verzeichnis'
export const FRONTEND_GROUP_FEED_PATH = '/groups-feed'
export const FRONTEND_FOLLOWEES_RECENT_FEED_PATH = '/followees-feed'
export const FRONTEND_LOCATION_PICKER_PATH = '/ort'

// ---------- sub routes ------------

export const CREATE_POST = '/create'
export const UPDATE_POST = '/update'
export const cleanUpdatePath = (path: string) =>
  path.replace(new RegExp('\/' + UPDATE_POST.substr(1) + '$'), '')

// ----------- urls param patterns ----

export const LOCATION_SLUG_PATTERN = getConfigValue('featureFlags.location.customLocationSlug') || ':locationSlug'
export const FILTER_PATTERN = ':filter'
export const PROFILES_DIRECTORY_CATEGORY_PATTERN = `:category(${PROFILE_LIST_URIS.join('|')})`
export const FRONTEND_EVENT_PARTICIPATING_PATH = 'teilgenommen'
export const CHAT_ROOM_PATTERN = ':roomId'
export const PROFILES_SEARCH_QUERY_KEY = 's'

// ---------- static pages ------------

export const FRONTEND_DATA_IMPRESSUM_PATH = apiHostURL('/static/imprint')
export const FRONTEND_DATA_AGB_PATH = apiHostURL('/static/terms_and_conditions')
export const FRONTEND_DATA_PROTECTION_PATH = apiHostURL('/static/privacy_policy')
export const FRONTEND_DATA_BLOG_PATH = getConfigValue('staticPages.urls.Blog')
export const FRONTEND_DATA_FAQ_PATH = getConfigValue('staticPages.urls.Faq')
export const FRONTEND_DATA_LEARN_MORE_PATH = getConfigValue('staticPages.urls.AboutUs')

// ---------- intents ----------

export const INTENT_CREATE_PUBLIC_PROFILE = 'create-public-profile'

// ---------- redirect urls -----------

export const REDIRECT_PARAMS_LOGIN_FACEBOOK = `ref=${SETTINGS_REFERRAL_FACEBOOK}&action=${SETTINGS_ACTION_LOGIN}`
export const REDIRECT_URL_PASSWORD_RESET = '/new-password'
export const REDIRECT_URL_REGISTRATION_EMAIL_CONFIRMATION =
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_EMAIL}&action=${SETTINGS_ACTION_REGISTRATION}`
export const REDIRECT_URL_REGISTRATION_EMAIL_CONFIRMATION_PUBLIC_PROFILE =
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_EMAIL}&action=${SETTINGS_ACTION_REGISTRATION}` +
  `&intent=${INTENT_CREATE_PUBLIC_PROFILE}`
export const REDIRECT_URL_REGISTRATION_FACEBOOK = (googlePlaceId: string) =>
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_FACEBOOK}&action=${SETTINGS_ACTION_REGISTRATION}` +
  `&gpid=${googlePlaceId}`
const REDIRECT_URL_REGISTRATION_FACEBOOK_WITH_EMAIL =
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_FACEBOOK}&action=${SETTINGS_ACTION_ADD_EMAIL}`
export const REDIRECT_EMAIL_CHANGE =
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_EMAIL}&action=${SETTINGS_ACTION_CHANGE}`
export const REDIRECT_PARAMS_LOGIN_APPLE = `ref=${SETTINGS_REFERRAL_APPLE}&action=${SETTINGS_ACTION_LOGIN}`
export const REDIRECT_URL_REGISTRATION_APPLE = (googlePlaceId: string) =>
  `${FRONTEND_SETTINGS_PATH}?ref=${SETTINGS_REFERRAL_APPLE}&action=${SETTINGS_ACTION_REGISTRATION}` +
  `&gpid=${googlePlaceId}`

// ---------- static URL paths ----------

export const STRUCTURED_DATA_LOGO_PATH =
  'https://s3.eu-central-1.amazonaws.com/lopo-static/images/logo-structured-data.png'
export const GOOGLE_PLAY_PATH = getConfigValue('links.googlePlayStore')
export const APP_STORE_PATH = getConfigValue('links.appleStore')

// ---------- WebSocket URL paths ----------

const WEBSOCKET_PROTOCOL = window.location.protocol === 'http:' ? 'ws://' : 'wss://'
export const WEBSOCKET_URL = WEBSOCKET_PROTOCOL + window.location.host + '/websocket'
